import { HStack, VStack } from '@chakra-ui/react';
import { Typography } from 'Tokens';
import { Metric, sortFrameworkMetrics } from './StandardsView';
import { FrameworkRefs } from '../../Assessment';
import { useMemo } from 'react';
import { MFStandardBrowserTable } from './MFStandardBrowserTable';
import { useParams } from 'react-router-dom';
import {
  GetMaterialStandardsIdsQuery_,
  GetParentAssessmentMaterialMetricsQuery_,
  GetParentMaterialityAssessmentQuery_,
  useGetMetricsForMfDrQuery,
} from 'models';
import { Loader } from 'Molecules';
import { MultiFrameCategoryAndStandards } from './DataPointsBank';
import { StandardMaterialityState } from '../DoubleMaterialityAssessment/DoubleMaterialityAssessment.d';

export const DrView = ({
  viewHistory,
  selectedDr,
  materialStandards,
  parentMaterialMetrics,
  frameworkList,
  selectedFrameworkRef,
  isCollectOnly,
  selectedmfStandard,
  parentMaterialStandards,
}: {
  viewHistory: JSX.Element;
  selectedDr?: {
    name: string;
    ref: string;
    metrics?: Metric[];
  };
  parentMaterialMetrics:
    | GetParentAssessmentMaterialMetricsQuery_['esrs_MaterialMetric']
    | undefined;
  materialStandards: GetMaterialStandardsIdsQuery_['esrs_MaterialStandard'] | undefined;
  frameworkList: string[];
  selectedFrameworkRef: string;
  isCollectOnly: boolean;
  selectedmfStandard?: MultiFrameCategoryAndStandards[number]['mfStandards'][number];
  parentMaterialStandards?: NonNullable<
    NonNullable<GetParentMaterialityAssessmentQuery_['EsrsAssessment_by_pk']>['parentAssessment']
  >['materialStandards'];
}) => {
  const { esrsAssessmentId = '', framework = '' } = useParams();
  const frameworkRefs = frameworkList.filter((framework) => {
    if (selectedFrameworkRef === 'all') {
      return framework !== 'all';
    }
    return framework === selectedFrameworkRef;
  });

  if (!frameworkRefs.includes(framework)) {
    frameworkRefs.push(framework);
  }

  const { data, loading } = useGetMetricsForMfDrQuery({
    variables: {
      assessmentId: esrsAssessmentId,
      frameworkRefs,
      mfDrRef: selectedDr?.ref,
      assessmentFrameworkRef: framework,
    },
    skip: !esrsAssessmentId || !selectedDr?.ref,
  });

  const frameworkStandard = useMemo(
    () => selectedmfStandard?.standards.find((standard) => standard.frameworkRef === framework),
    [selectedmfStandard, framework]
  );

  const isNotAssessedOrNotMaterial =
    frameworkStandard?.materiality === StandardMaterialityState.toAssess ||
    frameworkStandard?.materiality === StandardMaterialityState.notMaterial;

  const mfMetricsData = useMemo(
    () =>
      data?.MFDisclosureRequirementItem?.filter(
        (disclosureRequirementItem) =>
          !disclosureRequirementItem.metric.requirement.isMandatory ||
          disclosureRequirementItem.metric.requirement.frameworkRef !== framework
      )
        .sort((a, b) => sortFrameworkMetrics(a.metric as Metric, b.metric as Metric, framework))

        .map((disclosureRequirementItem) => {
          const met = disclosureRequirementItem.metric;
          const isOtherFramework = met.requirement.frameworkRef !== framework;
          const commonFrameworkMetricStandard = isOtherFramework
            ? data?.MFDisclosureRequirementItem.find(
                (drItem) => drItem.metric.requirement.frameworkRef === framework
              )?.metric.requirement.group.standard.reference
            : met.requirement.group.standard.reference;

          const materialStandardId =
            materialStandards?.find((ms) => ms.standardRef === commonFrameworkMetricStandard)?.id ??
            met.materialMetrics?.[0]?.materialStandardId;
          return {
            title: met.shortTitle ?? met.title,
            type: met.metricType,
            metric: met as Metric,
            framework: (met.requirement.frameworkRef ?? FrameworkRefs.esrs) as FrameworkRefs,
            reference: met.reference,
            subCategory: selectedDr?.name ?? '',
            description: met.description,
            materialStandardId,
          };
        })
        .filter((met) => selectedFrameworkRef === 'all' || met.framework === selectedFrameworkRef)
        ?.filter((m) => m.metric.isAssessable) ?? [],
    [data, framework, materialStandards, selectedDr, selectedFrameworkRef]
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <VStack
      gap={'32px'}
      justifyItems={'start'}
      alignSelf={'start'}
      padding={'24px 16px'}
      width={'100%'}
      alignItems={'start'}
      minH={'700px'}
      overflow={'auto'}
    >
      <VStack gap={'16px'} alignItems={'start'}>
        {viewHistory}
        <HStack>
          <Typography variant="h1">{selectedDr?.name}</Typography>
          <Typography color={'text.hint'}>{mfMetricsData.length}</Typography>
        </HStack>
      </VStack>
      <MFStandardBrowserTable
        mfDisclosures={mfMetricsData}
        isCollectOnly={isCollectOnly}
        parentMaterialMetrics={parentMaterialMetrics}
        hasPhasedInOption={false}
        isNotAssessedOrNotMaterial={isNotAssessedOrNotMaterial}
        isDrView={true}
        parentMaterialStandards={parentMaterialStandards}
      />
    </VStack>
  );
};
